/**
 * The decline reason the payment-recovery surfaces show (P7 F2-B6 → F2-F1).
 *
 * v3 sends `payment_problem.decline_message_key` = `errors:billing_decline_<code>` for a fixed allowlist of Stripe decline
 * codes, else `errors:billing_decline_generic` — never raw Stripe text. The FE accepts ONLY the keys it carries a sentence
 * for: any other value (a code v3 adds before the FE has its key, or a malformed one) reads as the generic sentence, so the
 * screen never prints a raw key ([I18N_KEY_MUST_EXIST]). Literal keys, so `check:i18n-keys` sees every one.
 */

const DECLINE_KEYS: ReadonlySet<string> = new Set([
  "errors:billing_decline_insufficient_funds",
  "errors:billing_decline_expired_card",
  "errors:billing_decline_incorrect_cvc",
  "errors:billing_decline_authentication_required",
  "errors:billing_decline_card_declined",
  "errors:billing_decline_do_not_honor",
]);

const GENERIC_DECLINE_KEY = "errors:billing_decline_generic";

/** The i18n key for a payment problem's decline reason; `null` when there is no payment-problem detail at all. */
export const declineMessageKey = (detail: { declineMessageKey: string | null } | null | undefined): string | null => {
  if (!detail) return null;

  return detail.declineMessageKey && DECLINE_KEYS.has(detail.declineMessageKey) ? detail.declineMessageKey : GENERIC_DECLINE_KEY;
};

/**
 * The hosted invoice page a billing manager may pay from — v3 sends it only to a viewer who can manage billing, and the FE
 * shows it only to one too. Only an https URL is ever rendered as a link (it comes from the Stripe mirror, but an href is
 * never trusted blindly). Opened in the same tab, like the Billing Portal.
 */
export const payInvoiceUrl = (detail: { hostedInvoiceUrl: string | null } | null | undefined, canManageBilling: boolean): string | null => {
  const url = detail?.hostedInvoiceUrl;

  return canManageBilling && typeof url === "string" && url.startsWith("https://") ? url : null;
};
